import { z } from 'zod';
import {
  supplierFactorAuthoritativeSefaV1Schema,
  supplierFactorEmissionsV1Schema,
  supplierFactorIndirectDetailV1Schema,
  supplierFactorIntervalV1Schema,
  supplierFactorNonNegativeNumberV1Schema,
  supplierFactorObjectIdV1Schema,
  supplierFactorSourceSefaEvidenceV1Schema,
} from './supplierFactorContracts';

// JS zod mirror of backend/src/contracts/supplierFactorDraft.ts — the S5-owned
// transitional draft proposal wire shape. The draft REVIEW surface (list/patch/
// discard/confirm routes) is S6-gated and NOT built here; this contract + its
// round-trip test land now because supplierFactorDraft.ts is committed.

export const SupplierFactorDraftReviewState = Object.freeze({
  Proposed: 'proposed',
  UnderReview: 'under_review',
  Discarded: 'discarded',
  Published: 'published',
});

export const supplierFactorDraftReviewStateV1Schema = z.nativeEnum(SupplierFactorDraftReviewState);

export const supplierFactorDraftSeriesKeyV1Schema = z
  .object({
    installationId: supplierFactorObjectIdV1Schema,
    cnCode: z.string().min(1),
    productVariant: z.string(),
    productionMethodCodes: z.array(z.string().min(1)),
    methodFingerprint: z.string(),
  })
  .strict();

export const supplierFactorDraftIntervalV1Schema = supplierFactorIntervalV1Schema;

/** targetSeriesKey as accepted from a client request — no server-computed methodFingerprint. */
export const supplierFactorDraftSeriesKeyRequestV1Schema = supplierFactorDraftSeriesKeyV1Schema
  .omit({ methodFingerprint: true })
  .extend({
    productVariant: z.string().optional(),
    productionMethodCodes: z.array(z.string().min(1)).optional(),
  })
  .strict();

export const supplierFactorDraftProposedFactorV1Schema = z
  .object({
    emissions: supplierFactorEmissionsV1Schema,
    authoritativeSefa: supplierFactorAuthoritativeSefaV1Schema,
    sourceSefaEvidence: supplierFactorSourceSefaEvidenceV1Schema.optional(),
    // S15 safe-deploy mirror (accept-and-ignore; S16 wires it into the form).
    indirectDetail: supplierFactorIndirectDetailV1Schema.optional(),
    cbamFactor: supplierFactorNonNegativeNumberV1Schema.max(100),
    reportingYear: z.number().int().min(2000).max(2200),
  })
  .strict();

const supplierFactorDraftCompanyValuesV1Schema = z
  .object({
    legalName: z.string().min(1).optional(),
    registeredCountryId: z.number().int().positive().optional(),
    contactEmail: z.string().email().optional(),
    verifiedDomain: z.string().min(1).optional(),
  })
  .strict();

const supplierFactorDraftInstallationValuesV1Schema = z
  .object({
    name: z.string().min(1).optional(),
    countryId: z.number().int().positive().optional(),
  })
  .strict();

export const SUPPLIER_FACTOR_DRAFT_LINEAGE_ORIGINS = ['template-upload', 'manual'];

const supplierFactorDraftLineageV1Schema = z
  .object({
    origin: z.enum(SUPPLIER_FACTOR_DRAFT_LINEAGE_ORIGINS),
    sourceUploadId: supplierFactorObjectIdV1Schema.optional(),
    sourceDiagramId: supplierFactorObjectIdV1Schema.optional(),
    parser: z
      .object({
        type: z.string().min(1),
        version: z.string().min(1),
      })
      .strict(),
  })
  .strict();

const supplierFactorDraftFieldValueV1Schema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

const supplierFactorDraftFieldProvenanceV1Schema = z
  .object({
    field: z.string().min(1),
    sourceValue: supplierFactorDraftFieldValueV1Schema,
    proposedValue: supplierFactorDraftFieldValueV1Schema,
    currentlyPublishedValue: supplierFactorDraftFieldValueV1Schema,
    source: z
      .object({
        kind: z.enum(['upload', 'diagram', 'parser', 'user']),
        recordId: supplierFactorObjectIdV1Schema.optional(),
        locator: z.string().min(1).optional(),
      })
      .strict(),
    confidence: z.number().finite().min(0).max(1).optional(),
  })
  .strict();

export const supplierFactorDraftV1Schema = z
  .object({
    version: z.literal(1),
    id: supplierFactorObjectIdV1Schema,
    operatorId: supplierFactorObjectIdV1Schema,
    reviewState: supplierFactorDraftReviewStateV1Schema,
    targetSeriesKey: supplierFactorDraftSeriesKeyV1Schema,
    interval: supplierFactorDraftIntervalV1Schema,
    proposedFactor: supplierFactorDraftProposedFactorV1Schema,
    proposedCompany: supplierFactorDraftCompanyValuesV1Schema.optional(),
    proposedInstallation: supplierFactorDraftInstallationValuesV1Schema.optional(),
    lineage: supplierFactorDraftLineageV1Schema,
    fieldProvenance: z.array(supplierFactorDraftFieldProvenanceV1Schema),
    publishedRevisionId: supplierFactorObjectIdV1Schema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

/* --------------------------------------------------------- S6 review routes */
// The draft REVIEW surface (list/patch/discard/confirm) landed on the backend
// with S6 — mirror its request/response envelopes here (S7.4/S7.5).

const supplierFactorDraftSeriesKeyPatchV1Schema = z
  .object({
    installationId: supplierFactorObjectIdV1Schema.optional(),
    cnCode: z.string().min(1).optional(),
    productVariant: z.string().optional(),
    productionMethodCodes: z.array(z.string().min(1)).optional(),
  })
  .strict();

const supplierFactorDraftProposedFactorPatchV1Schema = z
  .object({
    emissions: supplierFactorEmissionsV1Schema.partial().strict().optional(),
    authoritativeSefa: supplierFactorAuthoritativeSefaV1Schema.partial().strict().optional(),
    // S16: the review form now edits the breakdown, so the PATCH carries it
    // (S14 replacement semantics — a supplied object replaces, `{}` clears).
    indirectDetail: supplierFactorIndirectDetailV1Schema.optional(),
    cbamFactor: supplierFactorNonNegativeNumberV1Schema.max(100).optional(),
    reportingYear: z.number().int().min(2000).max(2200).optional(),
  })
  .strict();

// A supplier draft is bound to its source-evidence installation — the patch
// schema accepts `targetSeriesKey.installationId` structurally (so `.strict()`
// doesn't reject the key outright) but the superRefine below turns it into a
// validation error, matching the backend's own guard.
export const updateSupplierFactorDraftRequestV1Schema = z
  .object({
    version: z.literal(1),
    targetSeriesKey: supplierFactorDraftSeriesKeyPatchV1Schema.optional(),
    interval: supplierFactorDraftIntervalV1Schema.partial().strict().optional(),
    proposedFactor: supplierFactorDraftProposedFactorPatchV1Schema.optional(),
    proposedCompany: supplierFactorDraftCompanyValuesV1Schema.partial().strict().optional(),
    proposedInstallation: supplierFactorDraftInstallationValuesV1Schema
      .partial()
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.targetSeriesKey?.installationId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetSeriesKey', 'installationId'],
        message: 'A supplier draft is bound to its source-evidence installation',
      });
    }
  })
  .refine(
    (value) =>
      Boolean(
        value.targetSeriesKey ||
        value.interval ||
        value.proposedFactor ||
        value.proposedCompany ||
        value.proposedInstallation,
      ),
    { message: 'At least one proposed value must be supplied' },
  );

// POST /supplier/drafts — a supplier manually proposing a draft instead of
// publishing directly (S10 "draft-by-default"). Mirrors
// backend/src/contracts/supplierFactorDraft.ts
// createSupplierFactorDraftRequestV1Schema byte-for-byte: targetSeriesKey has
// no methodFingerprint (the server derives it), and proposedFactor carries
// authoritativeSefa with production/precursor REQUIRED-but-nullable (never
// omitted) — distinct from the publish/correct sefa shape in
// supplierFactorContracts.js, which omits those two keys entirely.
export const createSupplierFactorDraftRequestV1Schema = z
  .object({
    version: z.literal(1),
    targetSeriesKey: supplierFactorDraftSeriesKeyRequestV1Schema,
    interval: supplierFactorDraftIntervalV1Schema,
    proposedFactor: supplierFactorDraftProposedFactorV1Schema,
  })
  .strict();

// discard / confirm share this empty versioned command body.
export const supplierFactorDraftCommandRequestV1Schema = z
  .object({ version: z.literal(1) })
  .strict();

export const supplierFactorDraftResponseV1Schema = z
  .object({ version: z.literal(1), draft: supplierFactorDraftV1Schema })
  .strict();

export const supplierFactorDraftListResponseV1Schema = z
  .object({ version: z.literal(1), items: z.array(supplierFactorDraftV1Schema) })
  .strict();
